"use client";
import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import ButtonLink from "@/next-src/components/ui/button-link/ButtonLink";
import MembershipCard from "@/next-src/components/home/membership/membership-card/MembershipCard";
import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";
import Calendly from "@/next-src/components/ui/calendly/Calendly";

import comCSS from "./Membership.module.scss";

const Membership = ({ className = "", data = {} }) => {
  const { width: windowWidth } = useWindowDimensions();
  const {
    preTitleMobile,
    titleMobile,
    textMobile,
    buttonHeaderMobile,
    plans,
    buttonPlansMobile,
    bgColorMobile,
    calendlyButtonHeaderSuccess,
    calendlyButtonHeaderMobileSuccess,
    calendlyButtonPlansSuccess,
    calendlyButtonPlansMobileSuccess,
    paddingTopMobile,
    paddingBottomMobile,
  } = data || {};

  let {
    preTitle,
    title,
    text,
    buttonHeader,
    buttonPlans,
    bgColor,
    paddingTop,
    paddingBottom,
  } = data || {};

  if (windowWidth < 992) {
    preTitle = preTitleMobile;
    title = titleMobile;
    text = textMobile;
    buttonHeader = buttonHeaderMobile;
    buttonPlans = buttonPlansMobile;
    bgColor = bgColorMobile;
    paddingTop = paddingTopMobile;
    paddingBottom = paddingBottomMobile;
  }

  return (
    <section
      className={cn(
        className,
        comCSS.wrapper,
        "section background-color-soft-blue padding-section-large"
      )}
      style={{
        backgroundColor: bgColor,
        paddingTop:
          paddingTop !== null && paddingTop >= 0 ? `${paddingTop}px` : "",
        paddingBottom:
          paddingBottom !== null && paddingBottom >= 0
            ? `${paddingBottom}px`
            : "",
      }}
    >
      <div className="padding-global">
        <div className="container-large">
          <div className="center_component">
            {preTitle ||
            title ||
            text ||
            (!buttonHeader.hide && buttonHeader.text) ? (
              <div className="margin-bottom margin-large">
                <div className="text-align-center max-width-large">
                  <div className="max-width-large">
                    <div className="display-vertical-flex-center-top">
                      {preTitle ? (
                        <div className="margin-bottom margin-xsmall">
                          <div className="text-note">
                            {convertHTMLtoJSX(preTitle)}
                          </div>
                        </div>
                      ) : null}
                      {title ? (
                        <div className="margin-bottom  margin-small">
                          {convertHTMLtoJSX(title)}
                        </div>
                      ) : null}
                      {text ? (
                        <div className="margin-bottom margin-small">
                          <div className="text-size-medium">
                            {convertHTMLtoJSX(text)}
                          </div>
                        </div>
                      ) : null}

                      {buttonHeader.calendlyLink ? (
                        <Calendly
                          classNameBtn="custom-small-btn"
                          classNameBtnSuccess="custom-small-btn"
                          data={{
                            calendlyButton: buttonHeader,
                            calendlyButtonMobile: buttonHeader,
                            calendlyButtonSuccess: calendlyButtonHeaderSuccess,
                            calendlyButtonSuccessMobile:
                              calendlyButtonHeaderMobileSuccess,
                          }}
                        />
                      ) : (
                        <ButtonLink
                          data={buttonHeader}
                          className="custom-small-btn"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div
            className={cn(comCSS.cardsWrapper, "w-layout-grid _3-column-grid")}
          >
            {plans
              ?.filter((el) => !el.hide)
              ?.map((el) => (
                <MembershipCard key={el.id} data={el} />
              ))}
          </div>
          {!buttonPlans.hide && buttonPlans.text ? (
            <div className="display-vertical-flex-center-top">
              <div className="margin-top margin-large">
                {buttonPlans.calendlyLink ? (
                  <Calendly
                    classNameBtn="custom-small-btn"
                    classNameBtnSuccess="custom-small-btn"
                    data={{
                      calendlyButton: buttonPlans,
                      calendlyButtonMobile: buttonPlans,
                      calendlyButtonSuccess: calendlyButtonPlansSuccess,
                      calendlyButtonSuccessMobile:
                        calendlyButtonPlansMobileSuccess,
                    }}
                  />
                ) : (
                  <ButtonLink
                    data={buttonPlans}
                    className="custom-small-btn"
                    target="_blank"
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

Membership.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    preTitle: PropTypes.string,
    preTitleMobile: PropTypes.string,
    title: PropTypes.string,
    titleMobile: PropTypes.string,
    text: PropTypes.string,
    textMobile: PropTypes.string,
    buttonHeader: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    buttonHeaderMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonHeaderSuccess: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonHeaderMobileSuccess: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    plans: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        titleMobile: PropTypes.string,
        text: PropTypes.string,
        textMobile: PropTypes.string,
        button: PropTypes.shape({
          id: PropTypes.string,
          text: PropTypes.string,
          url: PropTypes.string,
          bgColor: PropTypes.string,
          bgColorHover: PropTypes.string,
          borderColor: PropTypes.string,
          borderColorHover: PropTypes.string,
          hide: PropTypes.bool,
        }),
        buttonMobile: PropTypes.shape({
          id: PropTypes.string,
          text: PropTypes.string,
          url: PropTypes.string,
          bgColor: PropTypes.string,
          bgColorHover: PropTypes.string,
          borderColor: PropTypes.string,
          borderColorHover: PropTypes.string,
          hide: PropTypes.bool,
        }),
        calendlyButtonSuccess: PropTypes.shape({
          id: PropTypes.string,
          text: PropTypes.string,
          url: PropTypes.string,
          bgColor: PropTypes.string,
          bgColorHover: PropTypes.string,
          borderColor: PropTypes.string,
          borderColorHover: PropTypes.string,
          hide: PropTypes.bool,
        }),
        calendlyButtonSuccessMobile: PropTypes.shape({
          id: PropTypes.string,
          text: PropTypes.string,
          url: PropTypes.string,
          bgColor: PropTypes.string,
          bgColorHover: PropTypes.string,
          borderColor: PropTypes.string,
          borderColorHover: PropTypes.string,
          hide: PropTypes.bool,
        }),
        icon: PropTypes.shape({
          id: PropTypes.string,
          src: PropTypes.string,
          alt: PropTypes.string,
        }),
        bgImage: PropTypes.shape({
          id: PropTypes.string,
          src: PropTypes.string,
          alt: PropTypes.string,
        }),
        recommended: PropTypes.shape({
          isActive: PropTypes.bool,
          text: PropTypes.string,
        }),
      })
    ),
    buttonPlans: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    buttonPlansMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonPlansSuccess: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonPlansMobileSuccess: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    everyPlanDetail: PropTypes.shape({}),
    paddingTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paddingBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paddingTopMobile: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paddingBottomMobile: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
};

export default Membership;
